import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Types
import {
  CheckEmailResponse,
  SpecialInstruction,
  SsoOrg,
} from '@app/account/account-api.model';
import {
  AccountHtmlId,
  AccountState,
  AccountUrls,
} from '@app/account/account-flow.model';
import { DgCaptchaError } from '@app/account/models/dg-captcha-error';

// Services
import { AccountService } from '@app/account/services/account.service';
import { AuthService } from '@app/shared/services/auth.service';
import {
  DfIconChevronRight12,
  DfIconInfo12,
  DfIconRegistry,
} from '@lib/fresco';
import { TranslateService } from '@ngx-translate/core';

// Utils
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

@Component({
  selector: 'dgx-login-form',
  templateUrl: './login-form.component.html',
})
export class LoginFormComponent implements OnInit {
  @Input() public captchaKey: string;
  @Input() public errors: string;
  @Input() public loginScope: string; // e.g. when embedded in native app
  @Input() public mobileSkip?: string;
  @Input() public antiForgeryToken: string;
  @Input() public passedData?: { username?: string };
  @Input() public returnUrl: string;
  @Input() public specialInstructions: string; // i.e. instructions from server to be i18n mapped
  @Input() public ssoId?: string;
  @Input() public username?: string;

  @Output() public goToState = new EventEmitter<any>();
  @Output() public preserveReturnUrl = new EventEmitter<any>();
  @Output() public setUtm = new EventEmitter<any>();

  // Elements
  @ViewChild('continueButton')
  public continueButton: ElementRef<any>;
  @ViewChild('hiddenAccountForm')
  public hiddenAccountForm: ElementRef<any>;
  @ViewChild('hiddenPasswordField')
  public hiddenPasswordField: ElementRef<any>;
  @ViewChild('hiddenUsernameField')
  public hiddenUsernameField: ElementRef<any>;
  @ViewChild('loginFormElement')
  public loginFormElement: ElementRef<any>;
  @ViewChild('passwordField', { static: false })
  public passwordField: ElementRef<any>;
  @ViewChild('ssoForm')
  public ssoForm: ElementRef<any>;
  @ViewChild('tokenField')
  public tokenField: ElementRef<any>;
  @ViewChild('usernameField')
  public usernameField: ElementRef<any>;

  // State & visibility
  public buttonSpin = false;
  public hasExtras = true;
  public loading = false;
  public showEmail = true;
  public showLoginUsingPassword = false;
  public showMoreFields = false;
  public willAutofill = false;
  public showSignup = false;
  public isOrgInvite = false;

  // Form
  public formAction: string;
  public greeting?: string;
  public hiddenFormAction: string;
  public nativeFormAction: string;
  public signupUrl: string;
  public specialInstructionsError: boolean;
  public user: { username?: string; password?: string; remember: boolean };
  public loginForm: FormGroup;
  public validation: { username: string; errors: string } = {
    username: '',
    errors: '',
  };

  // Hidden inputs
  public utmSource: string;
  public utmMedium: string;
  public utmCampaign: string;

  // Account Info
  public accountExists: boolean;
  public loginAtRemoteHost: boolean;

  // SSO
  public ssoType: string;
  public hasSso: boolean;
  public isMultiIdp: boolean;
  public isSso?: boolean;
  public multiIdpData: SsoOrg[];
  public ssoCanSignup: string;
  public ssoFormAction: string;
  public ssoOrg?: SsoOrg;
  public ssoTenant?: unknown;

  // Captcha
  public captcha?: string;
  public isValidCaptcha: boolean = true;
  public showCaptcha?: boolean;

  // Localization
  public readonly i18n = this.translate.instant([
    'Core_Or',
    'AccountController_EmailAddress',
    'AccountController_Continue',
    'AccountController_ContinueWithSSO',
    'AccountController_Forgot',
    'AccountController_RememberMe',
    'AccountController_LoginUsingPassword',
    'loginForm_EmailLabel',
    'loginForm_PasswordLabel',
    'loginForm_PasswordHelper',
    'loginForm_PasswordPlaceholder',
    'loginForm_PasswordPromptNoSSO',
    'loginForm_EmailStateTooltip',
    'loginForm_InvalidEmail',
    'loginForm_NoAccount',
    'loginForm_NoAccountCTA',
    'loginForm_SignUpNow',
    'loginForm_WelcomeTitle',
    'loginForm_WelcomeBackTitle',
  ]);

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private authService: AuthService,
    private accountService: AccountService,
    private builder: FormBuilder,
    private iconRegistry: DfIconRegistry
  ) {
    this.iconRegistry.registerIcons([DfIconInfo12, DfIconChevronRight12]);
  }

  public ngOnInit(): void {
    this.setForm();
    this.setOrgInvite();
    this.setGreeting();
    this.setSpecialInstructions();
    this.setUrls();
    this.setHiddenInputs();
    this.preserveReturnUrl.emit();
    this.setUtm.emit();
    this.prefillEmail();
    this.setFocusAndHandleAutofill();
    this.clearNgxAuthUser();
    this.skipWhenNative();
  }

  // Display
  public setGreeting(): void {
    this.greeting = this.isOrgInvite
      ? this.i18n.loginForm_WelcomeTitle
      : this.i18n.loginForm_WelcomeBackTitle;
  }

  public disableButton(): boolean {
    const enabled =
      this.isValidCaptcha &&
      ((this.willAutofill && !this.loginForm.get('username').touched) ||
        !this.loginForm.get('username').errors?.required);
    return !enabled;
  }

  public resetElements(): void {
    this.buttonSpin = false;
    this.clearUsernameMessage();
    // Required to access previously hidden elements, otherwise undefined
    this.cdr.detectChanges();
  }

  public setUrls(): void {
    this.formAction = AccountUrls.Login;
    this.hiddenFormAction = this.formAction;
    this.nativeFormAction = AccountUrls.NativeLogin;
    this.ssoFormAction = AccountUrls.Sso;
    this.signupUrl =
      AccountUrls.Signup +
      this.authService.appendReturnUrl('&', this.returnUrl);
  }

  public setSpecialInstructions(): void {
    const specialInstructions =
      this.specialInstructions !== ''
        ? this.translate.instant(`AccountSvc_${this.specialInstructions}`)
        : undefined;
    const hasPresetMessage = this.specialInstructions && specialInstructions;

    if (hasPresetMessage) {
      // i.e. server message is a predefined key to lookup message
      const message = this.specialInstructions;
      this.specialInstructions = this.translate.instant(
        `AccountSvc_${message}`
      );
      this.specialInstructionsError =
        this.accountService.isAccountError(message);
    }

    if (this.isOrgInvite) {
      this.showSignup = true;
    }

    // There's a case where special instructions is already set before we get to this function,
    // examples: notification unsubscribe, and during Provider SSO login, like from learnin.

    // In these cases you end up with a message like "AccountSvc_user@degreed.com has been successfully unsubscribed..."
    // A quick fix for this will be to just strip AccountSvc_ when we don't need it, and long term we'll want to understand
    // if there is a use case for specialInstructions coming through as already created messages or not, or if that in of itself is a bug.

    // Note: If message is not empty it gets appended to specialInstruction in account-forms.component.ts. It has not yet been determined why this occurs.

    if (this.specialInstructions && this.specialInstructions.length > 0) {
      this.specialInstructions = this.specialInstructions.replace(
        'AccountSvc_',
        ''
      );
    }
  }

  // Form
  public setForm(): void {
    this.loginForm = this.builder.group({
      username: [this.user?.username ?? undefined, [Validators.required]],
      password: [undefined, [Validators.required]],
      remember: [this.user?.remember ?? false],
    });
    this.validation.errors = this.errors;
    this.user = {
      username: this.username ?? '',
      remember: false,
    };
  }

  public submit = (event?: Event): void => {
    event?.preventDefault();
    this.setHiddenUsernameField();
    this.buttonSpin = true;
    if (this.showMoreFields) {
      this.removeHiddenPasswordField();
      this.loginFormElement.nativeElement.submit();
    } else {
      this.ssoId ? this.submitSso() : this.checkEmail();
    }
  };

  public focusField(element: HTMLInputElement): void {
    if (element) {
      element.focus();
    }
  }

  public enterToSubmit(event: KeyboardEvent): void {
    if (event?.keyCode === 13 && !this.disableButton()) {
      this.submit();
    }
  }

  public removeHiddenPasswordField(): void {
    // remove duplicate/hidden password field used for autocomplete
    const el = this.hiddenPasswordField.nativeElement;
    el?.parentNode?.removeChild(el);
  }

  public setHiddenUsernameField(): void {
    if (this.hiddenUsernameField?.nativeElement) {
      (this.hiddenUsernameField.nativeElement as HTMLInputElement).value =
        this.loginForm.get('username').value;
    }
  }

  public setChromeAutofill(): boolean {
    // Webkit (and Chrome) browsers don't autofill right away. Instead, for
    // security reasons, they wait for the first human interaction before
    // filling. Although we can't get the .value until the first interaction,
    // we're able to query :-webkit-autofill, which will tell us whether or not
    // the browser will autofill the input upon the first interaction.
    const chromeHasAutofill: HTMLElement = document.querySelector(
      `#username:-webkit-autofill`
    );
    const willAutofill = chromeHasAutofill !== null;
    this.willAutofill = willAutofill;
    return willAutofill;
  }

  public prefillEmail(): void {
    // Apply what's been passed to the component
    if (this.passedData) {
      this.user.username = this.passedData.username;
      if (this.user.username?.length) {
        this.loginForm.patchValue({
          username: this.user.username,
        });
        this.loginForm.updateValueAndValidity();
      }
    }
  }

  public setFocusAndHandleAutofill(): void {
    const hasUsername = this.user.username !== '';
    if (hasUsername) {
      this.showMoreFields = true;
      // nativeElement isn't available here in some cases (refresh on invalid password)
      this.focusField(
        document.getElementById(AccountHtmlId.Password) as HTMLInputElement
      );
    } else {
      // Needs time for nativeElement focus and chrome autofill to emerge
      setTimeout(() => {
        this.focusField(this.usernameField.nativeElement);
        this.setChromeAutofill();
      }, 500);
    }
  }

  public clearUsernameMessage(): void {
    this.validation.username = '';
  }

  public setOrgInvite(): void {
    this.isOrgInvite =
      this.specialInstructions === SpecialInstruction.OrgInvite;
  }

  public handleAccountExists(data: any): void {
    /**
     * https://degreedjira.atlassian.net/browse/PD-38432
     * Frontend forces `accountExists = true` to obfuscate real accounts
     */
    this.loginAtRemoteHost = !data.accountExists && data.remoteOrgHost;
    this.accountExists = true;
  }

  public processData(data: any): any {
    if (this.hasIdpData(data)) {
      data = this.processDataForIdp(data); // normalize `data` for IDP scenarios
    }
    return data;
  }

  public hasIdpData(data: any): boolean {
    return Array.isArray(data); // IDP scenario returns an array
  }

  public processDataForIdp(data: any): any {
    this.hasSso = true;
    if (data.length > 1) {
      // i.e. multi-IDP scenario
      this.isMultiIdp = true;
      this.multiIdpData = data;
    }
    // We're always use the 1st entry of the array as `data` here:
    // - In single-IDP scenarios: this is the only choice.
    // - In multi-IDP scenarios: it's temporary, since the user
    //   makes their selection in the very next step of the UI.
    // - In both: the 1st entry is representative for sniff-checks
    return data[0];
  }

  public checkEmail(): void {
    const captchaToken = this.showCaptcha ? this.captcha : null;
    const hasValue = this.loginForm.get('username').value?.length;

    if (!hasValue) {
      this.resetElements();
      return;
    }

    this.setHiddenUsernameField();

    // Captcha is handled at service level (checkEmail)
    this.accountService
      .checkEmail(
        this.loginForm.get('username').value,
        captchaToken,
        this.loginScope,
        this.returnUrl
      )
      .pipe(
        map((response: CheckEmailResponse) => {
          const data = this.processData(response);
          this.hasSso = data.id && data.name !== undefined;
          this.handleAccountExists(data);

          if (this.hasSso) {
            this.handleSso(data); // includes IDP/multi-IDP
          } else if (this.loginAtRemoteHost) {
            this.handleRemoteHost(data);
          } else if (this.accountExists) {
            this.handleExistingAccount();
          }

          this.resetElements();
        }),
        catchError(this.catchOnCheckEmail),
        finalize(() => {
          // Due to a bug in Angular, we need to force validation to prevent
          // form status from getting stuck in 'Pending' with async validation
          setTimeout(() => {
            this.loginForm.updateValueAndValidity();
          });
        })
      )
      .subscribe();
  }

  public catchOnCheckEmail = (
    error: Error | DgCaptchaError
  ): Observable<Error | DgCaptchaError> => {
    this.showMoreFields = true;
    this.specialInstructions = '';
    this.resetElements();
    if (error.hasOwnProperty('showCaptcha')) {
      this.resetCaptcha();
    }
    // Focus, but we need time for nativeElement to emerge from ngIf
    setTimeout(() => {
      this.focusField(this.passwordField.nativeElement);
    }, 500);
    return of(error);
  };

  // Remote Host
  public handleRemoteHost(data: any): void {
    const newUrl =
      data.remoteOrgHost +
      AccountUrls.RemoteOrgHostLogin +
      '?returnurl=' +
      this.returnUrl;

    this.hiddenFormAction = newUrl;
    this.showMoreFields = true;

    setTimeout(() => {
      const hiddenAccountFormEl = this.hiddenAccountForm
        .nativeElement as HTMLFormElement;
      hiddenAccountFormEl.submit();
      try {
        // Detect if this is via a pop-up (i.e. OAuth flow)
        const openedWindow = window.opener ?? window.parent;
        if (openedWindow) {
          // Message the parent about needing to switch its host (i.e. to eu.degreed.com)
          // calls ebb-auth.component.ts listenToSignIn
          openedWindow.postMessage(
            JSON.stringify({ dgHost: data.remoteOrgHost }),
            '*'
          );
        }
      } catch (e) {
        // Never mind, the window has closed or is unavailable
      }
    });
  }

  // SSO
  public submitSso(): void {
    // Using timeout to let newly changed form values apply before submitting
    setTimeout(() => {
      const ssoFormEl = this.ssoForm.nativeElement as HTMLFormElement;
      ssoFormEl.submit();
    });
  }

  public handleSso(data: SsoOrg): void {
    // Set state
    this.isSso = true;
    this.ssoOrg = data;
    this.ssoId = this.ssoOrg.id;
    this.ssoTenant = this.ssoOrg.tenant;
    this.ssoType = this.ssoOrg.ssoType;

    this.handleNonOpenId();

    // Adjust UI
    this.showLoginUsingPassword =
      !this.ssoOrg?.ssoOnly && this.ssoOrg?.allowPwReset && !!this.ssoId;
    this.hasExtras = this.showLoginUsingPassword;
    this.showEmail = false;
    this.showMoreFields = false;
    this.buttonSpin = false;
    this.validation.errors = ''; // clear previous since they carry over
    this.focusField(this.continueButton.nativeElement);
  }

  public handleNonOpenId(): void {
    // Non OpenID needs special instructions built up for our users.
    // {{specialInstructions}} binds with innerHTML, allowing us to wrap the
    // stylized translation into a container with basic styling. This keeps
    // this scenario consistent in style with the other flows.
    this.specialInstructions =
      '<div aria-live="polite" class="account__message par">' +
      this.translate.instant('loginForm_LoggingInAs', {
        startBold: '<strong class="block">',
        endBold: '</strong>',
        email: this.loginForm.get('username').value,
      }) +
      '</div>';
  }

  public chooseIdpOrg(id: string): void {
    const data: SsoOrg = this.multiIdpData.find(
      (idpData: SsoOrg) => idpData.id === id
    );

    // Re-apply property values based off user's selection
    // Some are tied to hidden form input values.
    this.ssoOrg = data;
    this.ssoId = data.id;
    this.ssoTenant = this.ssoOrg.tenant;
    this.ssoType = this.ssoOrg.ssoType;

    // Multi-IDP scenario uses non-submit <button>s since we need to update
    // form input values before submission. At this point, the form is ready,
    // so we can dynamically submit.
    this.loading = true;
    this.submit();
  }

  public stopSubmissionAndLoginUsingPassword(event: Event) {
    event.stopPropagation();
    this.loginUsingPassword();
  }

  // State/Flow
  public loginUsingPassword(): void {
    this.specialInstructions = '';
    this.showMoreFields = true;
    this.showEmail = true;
    this.hasExtras = false;
    // Needs time for nativeElement to emerge from ngIf
    setTimeout(() => {
      this.focusField(this.passwordField.nativeElement);
    }, 500);
  }

  public goToForgotPassword(): void {
    this.goToState.emit({
      toState: AccountState.ForgotPassword,
      passedData: {
        email: this.loginForm.get('username').value,
      },
    });
  }

  public handleExistingAccount(): void {
    this.specialInstructions = '';
    this.showMoreFields = true;
    this.clearUsernameMessage();
    // Needs time for nativeElement to emerge from ngIf
    setTimeout(() => {
      this.focusField(this.passwordField.nativeElement);
    }, 500);
  }

  // Native mobile
  public skipWhenNative(): void {
    if (this.mobileSkip) {
      this.showMoreFields = false;
      this.user.username = this.username;
      if (this.user.username?.length) {
        this.loginForm.patchValue({
          username: this.user.username,
        });
        this.loginForm.updateValueAndValidity();
      }
      this.formAction = this.nativeFormAction;
      this.hiddenFormAction = this.nativeFormAction;
      setTimeout(() => this.submit());
    }
  }

  // Captcha
  public resetCaptcha(): void {
    if (this.captcha) {
      window['grecaptcha'].reset();
    }
  }

  public validateCaptcha(response: any): boolean {
    // receive captcha values via header due to limitations on C# side
    this.showCaptcha = response.headers('X-ShowCaptcha') === 'true';
    const validToken = response.headers('X-ValidToken') === 'true';
    const validResponse = !(this.showCaptcha && !validToken);
    // e.g. return...
    // - `true` if no captcha's been shown
    // - `true` if captcha's shown and is successfully completed
    // - `false` if captcha's shown but isn't completed successfully
    return validResponse;
  }

  public recaptchaSuccess(captchaString: string): void {
    this.isValidCaptcha = true;
    this.captcha = captchaString;
    this.loginForm.patchValue({
      username: this.loginForm.get('username').value,
    });
    this.loginForm.updateValueAndValidity();
  }

  public recaptchaExpired(): void {
    this.isValidCaptcha = false;
  }

  // Utilities / Etc.
  public clearNgxAuthUser(): void {
    this.authService.clearAuth();
  }

  public setHiddenInputs(): void {
    // Using DOM access here since trying to use element nativeElements
    // here results in unreliable value access onInit
    const tokenEl = document.getElementsByName(
      AccountHtmlId.RequestVerificationToken
    )[0] as HTMLInputElement;
    const returnUrlEl = document.getElementById(
      AccountHtmlId.ReturnUrl
    ) as HTMLInputElement;
    const utmSourceEl = document.getElementById(
      AccountHtmlId.UtmSource
    ) as HTMLInputElement;
    const utmMediumEl = document.getElementById(
      AccountHtmlId.UtmMedium
    ) as HTMLInputElement;
    const utmCampaignEl = document.getElementById(
      AccountHtmlId.UtmCampaign
    ) as HTMLInputElement;
    this.antiForgeryToken = tokenEl.value;
    this.returnUrl = this.returnUrl ?? returnUrlEl.value;
    this.utmSource = utmSourceEl.value;
    this.utmMedium = utmMediumEl.value;
    this.utmCampaign = utmCampaignEl.value;
  }
}
